/**
 * A relationship's analyst marks in its detail (PRD-0075 R-C4, SC-013): no longer valid since a
 * date (with "valid again"), marked wrong (with "restore"), and the two actions. Presentational:
 * given what `relationshipInvalidation` and `retirementOf` read off the edge's properties.
 */
import { Ban, CalendarX2, RotateCcw, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/date'
import type { InvalidationView, RetirementView } from './fact-marks'

const T = 'analystFacts'

export interface RelationshipFactActions {
  onInvalidate: () => void
  onRevalidate: () => void
  onRetire: () => void
  onRestore: () => void
}

export interface RelationshipFactsViewProps {
  marks: { invalidation: InvalidationView | null; retirement: RetirementView | null }
  actions: RelationshipFactActions
  pending: boolean
}

function Banner({
  icon: Icon,
  text,
  action,
}: {
  icon: typeof Ban
  text: string
  action: { label: string; onClick: () => void; disabled: boolean; icon: typeof Ban }
}) {
  const ActionIcon = action.icon
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-2 py-1.5"
    >
      <Icon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 break-words text-foreground/85">{text}</span>
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled={action.disabled}
        onClick={action.onClick}
      >
        <ActionIcon aria-hidden />
        {action.label}
      </Button>
    </div>
  )
}

export function RelationshipFactsView({ marks, actions, pending }: RelationshipFactsViewProps) {
  const { t, i18n } = useTranslation()
  const day = (iso: string | null) => (iso ? formatDate(iso, i18n.language) : '')
  const unknown = t(`${T}.unknownAnalyst`)
  const { invalidation, retirement } = marks
  return (
    <section aria-label={t(`${T}.section`)} className="flex flex-col gap-1.5 text-2xs">
      {retirement && (
        <Banner
          icon={Ban}
          text={t(`${T}.retiredBanner`, {
            name: retirement.byName ?? unknown,
            date: day(retirement.at),
            reason: retirement.reason ?? '',
          })}
          action={{
            label: t(`${T}.actions.restore`),
            onClick: actions.onRestore,
            disabled: pending,
            icon: RotateCcw,
          }}
        />
      )}
      {invalidation && (
        <Banner
          icon={CalendarX2}
          text={t(`${T}.invalidBanner`, {
            date: day(invalidation.from),
            reason: invalidation.reason ?? '',
            name: invalidation.byName ?? unknown,
          })}
          action={{
            label: t(`${T}.actions.validAgain`),
            onClick: actions.onRevalidate,
            disabled: pending,
            icon: Undo2,
          }}
        />
      )}
      {!retirement && (
        <div className="flex flex-wrap items-center gap-1.5">
          {!invalidation && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              disabled={pending}
              onClick={actions.onInvalidate}
            >
              <CalendarX2 aria-hidden />
              {t(`${T}.actions.noLongerValid`)}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={pending}
            onClick={actions.onRetire}
          >
            <Ban aria-hidden />
            {t(`${T}.actions.markWrong`)}
          </Button>
        </div>
      )}
    </section>
  )
}
